/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { FocusHighlight } from '@atlassian/jira-portfolio-3-treegrid/src/ui/highlight/index.tsx';
import { useCellHighlight } from '@atlassian/jira-portfolio-3-treegrid/src/controllers/common/index.tsx';
import { useViewport } from '@atlassian/jira-portfolio-3-treegrid/src/controllers/container/index.tsx';
import { SECTION_COLLAPSE_THRESHOLD } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/grid/constants.tsx';

import { useRow } from '@atlassian/jira-portfolio-3-treegrid/src/controllers/row/index.tsx';
import { useCell } from '@atlassian/jira-portfolio-3-treegrid/src/controllers/cell/index.tsx';
import { HEADER_HEIGHT, HEIGHT_OFFSET } from './constants.tsx';
import type { Props } from './types.tsx';

export const EnhancedHighlight = ({ isCollapsed, columnCount }: Props) => {
	const { row } = useRow();
	const [{ column }] = useCell();
	const [{ height: viewportHeight }] = useViewport();
	const [highlight] = useCellHighlight();

	if (!isCollapsed || highlight !== true) {
		return <FocusHighlight />;
	}

	const numOfFields = columnCount - 2;

	if (column === columnCount - 1) {
		// if its a collapsed timeline column
		if (row === 0) {
			return (
				<div
					css={innerTimelineStyles}
					style={{ height: viewportHeight }}
					data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.table.utils.enhanced-highlight.timeline-highlight"
				/>
			);
		}
	}

	if (column >= 1 && column <= numOfFields) {
		/* if its a collapsed fields header cell return the highlight
		 *  component for a collapsed header field
		 */
		if (column === 1 && row === 0) {
			return (
				<div
					css={[highlightCollapsedFieldsStyles]}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
					style={{ height: viewportHeight - HEIGHT_OFFSET }}
					data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.table.utils.enhanced-highlight.fields-highlight"
				/>
			);
		}

		/* if its a collapsed subheader fields cell return the highlight
		 *  component for a collapsed subheader field
		 */
		if (row === 1) {
			return (
				<div
					css={[highlightCollapsedSubheaderFieldsStyles]}
					// The collapsed subheader field is in the second row, meaning we have to reduce
					// the height by the height of the top row
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
					style={{ height: viewportHeight - (HEADER_HEIGHT + HEIGHT_OFFSET) }}
					data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.table.utils.enhanced-highlight.field-subheader-highlight"
				/>
			);
		}
	}

	return <FocusHighlight />;
};

const highlightCollapsedFieldsStyles = css({
	inset: token('space.025'),
	outline: `2px solid ${token('color.border.focused')}`,
	position: 'absolute',
	overflow: 'hidden',
	pointerEvents: 'none',
});

const highlightCollapsedSubheaderFieldsStyles = css({
	inset: token('space.025'),
	outline: `2px solid ${token('color.border.focused')}`,
	position: 'absolute',
	overflow: 'hidden',
	pointerEvents: 'none',
});

const innerTimelineStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	width: `${SECTION_COLLAPSE_THRESHOLD}px`,
	pointerEvents: 'none',
	position: 'absolute',
	outline: `2px solid ${token('color.border.focused')}`,
	outlineOffset: token('space.negative.025'),
});
